<script setup lang="ts">
import type { Fields } from '@voix/composables/useSlice'
import type {
  ElementInterface,
  SliceInterface,
  SliceSettingsInterface,
} from '@voix/types'

import type { ComputedRef, Ref } from 'vue'
import { useNuxtApp, useState } from '#imports'

import { useElementInView } from '@voix/composables/useElementInView'

import { useHighlightElement } from '@voix/composables/useHighlightElement'
import { useSlice } from '@voix/composables/useSlice'

import { useUtils } from '@voix/composables/useUtils'
import { useAdminStore } from '@voix/store/adminStore'

import { usePageStore } from '@voix/store/pageStore'
import { useSliceStore } from '@voix/store/sliceStore'
import { vElementBounding } from '@vueuse/components'
import { useDebounceFn, useWindowSize } from '@vueuse/core'

import { computed, inject, onMounted, provide, ref, useCssModule, useTemplateRef, watch } from 'vue'
import { findBreakpointSettings } from '../../composables/useBreakpoints'

import {
  buildColorStyles,
  buildFlexStyles,
  buildGridStyles,
  buildMarginStyles,
  buildMaxWidthStyles,
  buildPaddingStyles,
} from '../../composables/useStyle'

defineOptions({ inheritAttrs: false })
const props = defineProps<{
  slice: SliceInterface
}>()
// Import all the slices dynamically
// voixSliceTransform.ts will automatically add the import statements
// like `import { LazySlicesCircleSlideshow } from '#components'` to
// this file at the comment below

// DO NOT REMOVE THESE LINES WHERE WE DEFINE components
let components: Record<string, any> = {}

      import { LazySlicesAgentsPortalDashboard } from '#components'
import { LazySlicesAgentsPortalHero } from '#components'
import { LazySlicesAgentsPortalLogin } from '#components'
import { LazySlicesAgentsPortalLoginRequiredButton } from '#components'
import { LazySlicesAgentsPortalPopup } from '#components'
import { LazySlicesAgentsPortalSection } from '#components'
import { LazySlicesAgentsPortalSpecialsWithFilters } from '#components'
import { LazySlicesAgentsPortalSproutloudSsoBlock } from '#components'
import { LazySlicesAgentsPortalStaff } from '#components'
import { LazySlicesAgentsPortalWhiteContentIconFeatureCard } from '#components'
import { LazySlicesBlocksBlockLink } from '#components'
import { LazySlicesBlocksBlockOverImage } from '#components'
import { LazySlicesBlocksBlockSpecials } from '#components'
import { LazySlicesBlocksBlocksHero } from '#components'
import { LazySlicesBlocksBrandCard } from '#components'
import { LazySlicesBlocksBrandResortFilters } from '#components'
import { LazySlicesBlocksBrandResortListing } from '#components'
import { LazySlicesBlocksBrandsSection } from '#components'
import { LazySlicesBlocksFeaturedPlaceSection } from '#components'
import { LazySlicesBlocksFeaturesSection } from '#components'
import { LazySlicesBlocksImageSliderWithContent } from '#components'
import { LazySlicesBlocksIncludedWithStay } from '#components'
import { LazySlicesBlocksNewsletterFormSection } from '#components'
import { LazySlicesBlocksSoloMedia } from '#components'
import { LazySlicesBlocksTermsConditionsSection } from '#components'
import { LazySlicesBonitaAllIncluded } from '#components'
import { LazySlicesBonitaBackgroundImageWithCard } from '#components'
import { LazySlicesBonitaDoubleImageIntro } from '#components'
import { LazySlicesBonitaFeature } from '#components'
import { LazySlicesBonitaFeatureGalleryImage } from '#components'
import { LazySlicesBonitaFeatures } from '#components'
import { LazySlicesBonitaFeaturesBlock } from '#components'
import { LazySlicesBonitaGalleriesMagazineSlider } from '#components'
import { LazySlicesBonitaImageFlankingTitleCopy } from '#components'
import { LazySlicesBonitaImageTitleCopyCard } from '#components'
import { LazySlicesBonitaTwoColumnContainer } from '#components'
import { LazySlicesGlobalAnniversarySweepstakesForm } from '#components'
import { LazySlicesGlobalBookingDatesLimiter } from '#components'
import { LazySlicesGlobalBookingExcludeResorts } from '#components'
import { LazySlicesGlobalContactBlock } from '#components'
import { LazySlicesGlobalCountdownClock } from '#components'
import { LazySlicesGlobalDownloadLink } from '#components'
import { LazySlicesGlobalFactSheets } from '#components'
import { LazySlicesGlobalGhostButtonLink } from '#components'
import { LazySlicesGlobalGlobalImage } from '#components'
import { LazySlicesGlobalHtml5Video } from '#components'
import { LazySlicesGlobalIframe } from '#components'
import { LazySlicesGlobalImage } from '#components'
import { LazySlicesGlobalImageCard } from '#components'
import { LazySlicesGlobalLogo } from '#components'
import { LazySlicesGlobalNumberedImageContentCard } from '#components'
import { LazySlicesGlobalPromoCoupon } from '#components'
import { LazySlicesGlobalRmCartButtonCode } from '#components'
import { LazySlicesGlobalSafeStay } from '#components'
import { LazySlicesGlobalSafeStayBar } from '#components'
import { LazySlicesGlobalSealGraphicWithText } from '#components'
import { LazySlicesGlobalSignUpPopUp } from '#components'
import { LazySlicesGlobalSkewedThreeLines } from '#components'
import { LazySlicesGlobalSliderImage } from '#components'
import { LazySlicesGlobalSliderImageCard } from '#components'
import { LazySlicesGlobalStyleGuide } from '#components'
import { LazySlicesGlobalStyledNumberWithText } from '#components'
import { LazySlicesGlobalTripAdvisor } from '#components'
import { LazySlicesGlobalYoutube } from '#components'
import { LazySlicesGlobalYoutubePlayer } from '#components'
import { LazySlicesGlobalBookingInlineBookingWidget } from '#components'
import { LazySlicesGlobalBookingInlineFlightBookingWidget } from '#components'
import { LazySlicesGlobalBookingLargeBookingWidget } from '#components'
import { LazySlicesGlobalContainersBackgroundImageContainer } from '#components'
import { LazySlicesGlobalContainersContentContainer } from '#components'
import { LazySlicesGlobalContainersFullWidthContainer } from '#components'
import { LazySlicesGlobalContainersGridOfCards } from '#components'
import { LazySlicesGlobalContainersStrippedList } from '#components'
import { LazySlicesGlobalContainersTitleListColumn } from '#components'
import { LazySlicesGlobalContainersTwoColContainer } from '#components'
import { LazySlicesGlobalCredibilityCredibilitySlider } from '#components'
import { LazySlicesGlobalErrorsFourOhFour } from '#components'
import { LazySlicesGlobalFormsAgentSearch } from '#components'
import { LazySlicesGlobalFormsBrochureRequest } from '#components'
import { LazySlicesGlobalFormsGroups } from '#components'
import { LazySlicesGlobalFormsGroupsShort } from '#components'
import { LazySlicesGlobalFormsHandbookLogin } from '#components'
import { LazySlicesGlobalFormsIdMe } from '#components'
import { LazySlicesGlobalGalleriesGridGallery } from '#components'
import { LazySlicesGlobalGalleriesMagazineSlide } from '#components'
import { LazySlicesGlobalGalleriesMagazineSlider } from '#components'
import { LazySlicesGlobalGalleriesGridGalleryElementsContentBox } from '#components'
import { LazySlicesGlobalGalleriesGridGalleryElementsGridImage } from '#components'
import { LazySlicesGlobalGalleriesGridGalleryElementsImage } from '#components'
import { LazySlicesGlobalHeroesHeroImage } from '#components'
import { LazySlicesGlobalHeroesHeroSlider } from '#components'
import { LazySlicesGlobalHeroesVimeoHero } from '#components'
import { LazySlicesGlobalIntroductionsColumnsAndPictures } from '#components'
import { LazySlicesGlobalIntroductionsFullWidthWysiwyg } from '#components'
import { LazySlicesGlobalIntroductionsTitleParagraph } from '#components'
import { LazySlicesGlobalIntroductionsTitleSubtitle } from '#components'
import { LazySlicesGlobalOverridesShowCovid19Info } from '#components'
import { LazySlicesGlobalSlidersGeneralSlider } from '#components'
import { LazySlicesGlobalSmallPiecesLearnMoreModal } from '#components'
import { LazySlicesGlobalTablesContactInformationRow } from '#components'
import { LazySlicesGlobalTablesTable } from '#components'
import { LazySlicesGlobalTablesTextRow } from '#components'
import { LazySlicesHyattAmenaties } from '#components'
import { LazySlicesHyattBackgroundImageContentBlock } from '#components'
import { LazySlicesHyattBanner } from '#components'
import { LazySlicesHyattIntroductionTwoColumn } from '#components'
import { LazySlicesHyattLeftRightBlockSuite } from '#components'
import { LazySlicesHyattLeftRightCurveBlock } from '#components'
import { LazySlicesHyattLeftRightHalfBlock } from '#components'
import { LazySlicesHyattRainbowBar } from '#components'
import { LazySlicesHyattSpecialOffer } from '#components'
import { LazySlicesHyattSwooshMaskedImagesAndColumns } from '#components'
import { LazySlicesHyattTwentyTwentyTwoAllIncluded } from '#components'
import { LazySlicesHyattTwentyTwentyTwoFracturedGallery } from '#components'
import { LazySlicesHyattTwentyTwentyTwoMagazineOverlay } from '#components'
import { LazySlicesHyattTwentyTwentyTwoNavyBlockOverlay } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSignatureTitle } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSlantedGallery } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSmallImageSmallCopy } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSpecialOffer } from '#components'
import { LazySlicesHyattTwentyTwentyTwoTwoImageDescriptionOverlay } from '#components'
import { LazySlicesHyattSmallPiecesSuiteDetails } from '#components'
import { LazySlicesInfinityLuxuryContactForm } from '#components'
import { LazySlicesInfinityLuxuryInfinityHero } from '#components'
import { LazySlicesInfinityLuxuryInfinityTextSlice } from '#components'
import { LazySlicesInfinityLuxuryMagazineLayout } from '#components'
import { LazySlicesInfinityLuxuryBookingMemberBookingWidget } from '#components'
import { LazySlicesKimptonAllIncluded } from '#components'
import { LazySlicesKimptonBanner } from '#components'
import { LazySlicesKimptonCopyCard } from '#components'
import { LazySlicesKimptonExpandableContentBlock } from '#components'
import { LazySlicesKimptonHero } from '#components'
import { LazySlicesKimptonHorizontalCard } from '#components'
import { LazySlicesKimptonImageCard } from '#components'
import { LazySlicesKimptonIntro } from '#components'
import { LazySlicesKimptonMagazineCard } from '#components'
import { LazySlicesKimptonPromoGrid } from '#components'
import { LazySlicesKimptonScrollingGallery } from '#components'
import { LazySlicesKimptonScrollingGalleryImage } from '#components'
import { LazySlicesKimptonStandardContentBlock } from '#components'
import { LazySlicesKimptonStandardCopy } from '#components'
import { LazySlicesKimptonTwoColumns } from '#components'
import { LazySlicesPanamaJackAlwaysIncluded } from '#components'
import { LazySlicesPlayaCaresFullWidthCountries } from '#components'
import { LazySlicesPlayaCaresFullWidthStats } from '#components'
import { LazySlicesPlayaCollectionAllIncluded } from '#components'
import { LazySlicesPlayaCollectionAwardsSlider } from '#components'
import { LazySlicesPlayaCollectionBlockOverImage } from '#components'
import { LazySlicesPlayaCollectionCallToAction } from '#components'
import { LazySlicesPlayaCollectionCenteredTextParallax } from '#components'
import { LazySlicesPlayaCollectionColorBarTitleParagraph } from '#components'
import { LazySlicesPlayaCollectionContentGrid } from '#components'
import { LazySlicesPlayaCollectionFeatures } from '#components'
import { LazySlicesPlayaCollectionGridItemTitleDescription } from '#components'
import { LazySlicesPlayaCollectionHero } from '#components'
import { LazySlicesPlayaCollectionHotelList } from '#components'
import { LazySlicesPlayaCollectionTestimonial } from '#components'
import { LazySlicesPlayaCollectionThreeImageCards } from '#components'
import { LazySlicesPlayaCollectionTitleDescription } from '#components'
import { LazySlicesPlayaCollectionTwoPaneTextImage } from '#components'
import { LazySlicesPlayaCollectionBookingMemberBookingWidget } from '#components'
import { LazySlicesPlayaresortsCheckInOutTimes } from '#components'
import { LazySlicesPlayaresortsContactRow } from '#components'
import { LazySlicesPlayaresortsImageTitleCopyLink } from '#components'
import { LazySlicesPlayaresortsLeftRightBlockSuite } from '#components'
import { LazySlicesPlayaresortsLeftRightImageWithText } from '#components'
import { LazySlicesPlayaresortsPlayaresortsLeftRightBlockSuite } from '#components'
import { LazySlicesPlayaresortsTitleWysiwyg } from '#components'
import { LazySlicesPlayaresortsVerticalImageCard } from '#components'
import { LazySlicesPlayaresortsWeWantYourInput } from '#components'
import { LazySlicesPlayaresortsWideImageBgSmallWhiteBox } from '#components'
import { LazySlicesPlayaresortsBlogPostsBlogAll } from '#components'
import { LazySlicesPlayaresortsBlogPostsBlogPostDetails } from '#components'
import { LazySlicesPlayaresortsBrandsRowsWithFilters } from '#components'
import { LazySlicesPlayaresortsPressReleasesPressReleasesAll } from '#components'
import { LazySlicesPlayaresortsPressReleasesPressReleasesSingle } from '#components'
import { LazySlicesPlayaresortsPrivacyPolicyTable } from '#components'
import { LazySlicesPlayaresortsPrivacyPolicyTableRow } from '#components'
import { LazySlicesPlayaresortsResortsMeetingFacilitiesExplorer } from '#components'
import { LazySlicesPlayaresortsSpecialOffersPageSpecials } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferBlock } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferCard } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferGrid } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferHorizontal } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferSlider } from '#components'
import { LazySlicesSanctuaryContact } from '#components'
import { LazySlicesSanctuaryFadeColorBlock } from '#components'
import { LazySlicesSanctuaryFeathersDivider } from '#components'
import { LazySlicesSanctuaryFullWidthSpecialOffer } from '#components'
import { LazySlicesSanctuaryGoldPromotion } from '#components'
import { LazySlicesSanctuaryImageBlock } from '#components'
import { LazySlicesSanctuaryIncludedWithStay } from '#components'
import { LazySlicesSanctuaryIntroductionsTitleParagraph } from '#components'
import { LazySlicesSanctuaryLeftRightHalfBlock } from '#components'
import { LazySlicesSanctuaryRoomListing } from '#components'
import { LazySlicesSanctuarySpecialOffer } from '#components'
import { LazySlicesSanctuaryTwoColumnCopyAndImage } from '#components'
import { LazySlicesSanctuaryVideoTakeover } from '#components'
import { LazySlicesSandpiperAboutRoomes } from '#components'
import { LazySlicesSandpiperAccordionCopy } from '#components'
import { LazySlicesSandpiperActivityGrid } from '#components'
import { LazySlicesSandpiperActivityGridCard } from '#components'
import { LazySlicesSandpiperAlertBlock } from '#components'
import { LazySlicesSandpiperBackgroundShape } from '#components'
import { LazySlicesSandpiperColorCardGrid } from '#components'
import { LazySlicesSandpiperColorCardGridCard } from '#components'
import { LazySlicesSandpiperCopyBesideImages } from '#components'
import { LazySlicesSandpiperGeneralTitleAndCopy } from '#components'
import { LazySlicesSandpiperIncludedWithStay } from '#components'
import { LazySlicesSandpiperMap } from '#components'
import { LazySlicesSandpiperRoomType } from '#components'
import { LazySlicesSandpiperRoomTypeFilter } from '#components'
import { LazySlicesSandpiperRoomTypes } from '#components'
import { LazySlicesSandpiperSandpiperHeroImage } from '#components'
import { LazySlicesSandpiperSandpiperSpecialOfferSlider } from '#components'
import { LazySlicesSeadustContentSeadustBlog } from '#components'
import { LazySlicesSeadustContentSeadustBlogDetails } from '#components'
import { LazySlicesSeadustContentSeadustConferenceRoom } from '#components'
import { LazySlicesSeadustContentSeadustConferenceRoomFeature } from '#components'
import { LazySlicesSeadustContentSeadustContactForm } from '#components'
import { LazySlicesSeadustContentSeadustFullWidthGallery } from '#components'
import { LazySlicesSeadustContentSeadustFullWidthGalleryImage } from '#components'
import { LazySlicesSeadustContentSeadustGallery } from '#components'
import { LazySlicesSeadustContentSeadustGalleryList } from '#components'
import { LazySlicesSeadustContentSeadustGalleryListBlock } from '#components'
import { LazySlicesSeadustContentSeadustGenericFullWidthBgWhiteBox } from '#components'
import { LazySlicesSeadustContentSeadustGenericListItem } from '#components'
import { LazySlicesSeadustContentSeadustGenericListItemImage } from '#components'
import { LazySlicesSeadustContentSeadustMap } from '#components'
import { LazySlicesSeadustContentSeadustRestaurantList } from '#components'
import { LazySlicesSeadustContentSeadustRestaurantListBlock } from '#components'
import { LazySlicesSeadustContentSeadustRightParagraphGridImages } from '#components'
import { LazySlicesSeadustContentSeadustRoomDetails } from '#components'
import { LazySlicesSeadustContentSeadustSafeVideoNavigation } from '#components'
import { LazySlicesSeadustContentSeadustSandyThreeColGrid } from '#components'
import { LazySlicesSeadustContentSeadustSandyThreeColGridItem } from '#components'
import { LazySlicesSeadustContentSeadustServicesIconsList } from '#components'
import { LazySlicesSeadustContentSeadustServicesIconsListItem } from '#components'
import { LazySlicesSeadustContentSeadustSitemap } from '#components'
import { LazySlicesSeadustContentSeadustTitleParagraph } from '#components'
import { LazySlicesSeadustContentSeadustWeddingsForm } from '#components'
import { LazySlicesSeadustFormsSeadustConferenceRoomRequestForm } from '#components'
import { LazySlicesSeadustGraphicalLinksSeadustFiftyFiftyImageLinks } from '#components'
import { LazySlicesSeadustGraphicalLinksSeadustFiveImageGridLinks } from '#components'
import { LazySlicesSeadustSlidersFullWidth } from '#components'
import { LazySlicesSeadustSlidersFullWidthSlide } from '#components'
import { LazySlicesSeadustSlidersSeadustBlocksSlide } from '#components'
import { LazySlicesSeadustSlidersSeadustBlocksSlider } from '#components'
import { LazySlicesSeadustSlidersSeadustLeftAlignContentSlide } from '#components'
import { LazySlicesSeadustSlidersSeadustLeftAlignContentWithSlider } from '#components'
import { LazySlicesSeadustSlidersSeadustParalaxBlocksSlide } from '#components'
import { LazySlicesSeadustSlidersSeadustParalaxBlocksSlider } from '#components'
import { LazySlicesSeadustThreeSixtyToursSeadustSpaziousTour } from '#components'
import { LazySlicesWeddingsBgSideContentCallToAction } from '#components'
import { LazySlicesWeddingsCard } from '#components'
import { LazySlicesWeddingsContactDetails } from '#components'
import { LazySlicesWeddingsFullWidthImage } from '#components'
import { LazySlicesWeddingsPackagesSlide } from '#components'
import { LazySlicesWeddingsPackagesSlider } from '#components'
import { LazySlicesWeddingsPatternedGrid } from '#components'
import { LazySlicesWeddingsTitleParagraphDivider } from '#components'
import { LazySlicesWeddingsTwoColumnIntroduction } from '#components'
import { LazySlicesWeddingsVenueSlide } from '#components'
import { LazySlicesWeddingsVenueSlider } from '#components'
import { LazySlicesWyndhamCardOverImages } from '#components'
import { LazySlicesWyndhamDualImageCards } from '#components'
import { LazySlicesWyndhamFullWidthImageCardOnBottom } from '#components'
import { LazySlicesWyndhamHideSignUpToSave } from '#components'
import { LazySlicesWyndhamHideWyndhamInlineBookingWidget } from '#components'
import { LazySlicesWyndhamInlineBookingWidget } from '#components'
import { LazySlicesWyndhamTallSixImageCards } from '#components'
import { LazySlicesWyndhamTallThreeImageCards } from '#components'

     components = {
      LazySlicesAgentsPortalDashboard,
  LazySlicesAgentsPortalHero,
  LazySlicesAgentsPortalLogin,
  LazySlicesAgentsPortalLoginRequiredButton,
  LazySlicesAgentsPortalPopup,
  LazySlicesAgentsPortalSection,
  LazySlicesAgentsPortalSpecialsWithFilters,
  LazySlicesAgentsPortalSproutloudSsoBlock,
  LazySlicesAgentsPortalStaff,
  LazySlicesAgentsPortalWhiteContentIconFeatureCard,
  LazySlicesBlocksBlockLink,
  LazySlicesBlocksBlockOverImage,
  LazySlicesBlocksBlockSpecials,
  LazySlicesBlocksBlocksHero,
  LazySlicesBlocksBrandCard,
  LazySlicesBlocksBrandResortFilters,
  LazySlicesBlocksBrandResortListing,
  LazySlicesBlocksBrandsSection,
  LazySlicesBlocksFeaturedPlaceSection,
  LazySlicesBlocksFeaturesSection,
  LazySlicesBlocksImageSliderWithContent,
  LazySlicesBlocksIncludedWithStay,
  LazySlicesBlocksNewsletterFormSection,
  LazySlicesBlocksSoloMedia,
  LazySlicesBlocksTermsConditionsSection,
  LazySlicesBonitaAllIncluded,
  LazySlicesBonitaBackgroundImageWithCard,
  LazySlicesBonitaDoubleImageIntro,
  LazySlicesBonitaFeature,
  LazySlicesBonitaFeatureGalleryImage,
  LazySlicesBonitaFeatures,
  LazySlicesBonitaFeaturesBlock,
  LazySlicesBonitaGalleriesMagazineSlider,
  LazySlicesBonitaImageFlankingTitleCopy,
  LazySlicesBonitaImageTitleCopyCard,
  LazySlicesBonitaTwoColumnContainer,
  LazySlicesGlobalAnniversarySweepstakesForm,
  LazySlicesGlobalBookingDatesLimiter,
  LazySlicesGlobalBookingExcludeResorts,
  LazySlicesGlobalContactBlock,
  LazySlicesGlobalCountdownClock,
  LazySlicesGlobalDownloadLink,
  LazySlicesGlobalFactSheets,
  LazySlicesGlobalGhostButtonLink,
  LazySlicesGlobalGlobalImage,
  LazySlicesGlobalHtml5Video,
  LazySlicesGlobalIframe,
  LazySlicesGlobalImage,
  LazySlicesGlobalImageCard,
  LazySlicesGlobalLogo,
  LazySlicesGlobalNumberedImageContentCard,
  LazySlicesGlobalPromoCoupon,
  LazySlicesGlobalRmCartButtonCode,
  LazySlicesGlobalSafeStay,
  LazySlicesGlobalSafeStayBar,
  LazySlicesGlobalSealGraphicWithText,
  LazySlicesGlobalSignUpPopUp,
  LazySlicesGlobalSkewedThreeLines,
  LazySlicesGlobalSliderImage,
  LazySlicesGlobalSliderImageCard,
  LazySlicesGlobalStyleGuide,
  LazySlicesGlobalStyledNumberWithText,
  LazySlicesGlobalTripAdvisor,
  LazySlicesGlobalYoutube,
  LazySlicesGlobalYoutubePlayer,
  LazySlicesGlobalBookingInlineBookingWidget,
  LazySlicesGlobalBookingInlineFlightBookingWidget,
  LazySlicesGlobalBookingLargeBookingWidget,
  LazySlicesGlobalContainersBackgroundImageContainer,
  LazySlicesGlobalContainersContentContainer,
  LazySlicesGlobalContainersFullWidthContainer,
  LazySlicesGlobalContainersGridOfCards,
  LazySlicesGlobalContainersStrippedList,
  LazySlicesGlobalContainersTitleListColumn,
  LazySlicesGlobalContainersTwoColContainer,
  LazySlicesGlobalCredibilityCredibilitySlider,
  LazySlicesGlobalErrorsFourOhFour,
  LazySlicesGlobalFormsAgentSearch,
  LazySlicesGlobalFormsBrochureRequest,
  LazySlicesGlobalFormsGroups,
  LazySlicesGlobalFormsGroupsShort,
  LazySlicesGlobalFormsHandbookLogin,
  LazySlicesGlobalFormsIdMe,
  LazySlicesGlobalGalleriesGridGallery,
  LazySlicesGlobalGalleriesMagazineSlide,
  LazySlicesGlobalGalleriesMagazineSlider,
  LazySlicesGlobalGalleriesGridGalleryElementsContentBox,
  LazySlicesGlobalGalleriesGridGalleryElementsGridImage,
  LazySlicesGlobalGalleriesGridGalleryElementsImage,
  LazySlicesGlobalHeroesHeroImage,
  LazySlicesGlobalHeroesHeroSlider,
  LazySlicesGlobalHeroesVimeoHero,
  LazySlicesGlobalIntroductionsColumnsAndPictures,
  LazySlicesGlobalIntroductionsFullWidthWysiwyg,
  LazySlicesGlobalIntroductionsTitleParagraph,
  LazySlicesGlobalIntroductionsTitleSubtitle,
  LazySlicesGlobalOverridesShowCovid19Info,
  LazySlicesGlobalSlidersGeneralSlider,
  LazySlicesGlobalSmallPiecesLearnMoreModal,
  LazySlicesGlobalTablesContactInformationRow,
  LazySlicesGlobalTablesTable,
  LazySlicesGlobalTablesTextRow,
  LazySlicesHyattAmenaties,
  LazySlicesHyattBackgroundImageContentBlock,
  LazySlicesHyattBanner,
  LazySlicesHyattIntroductionTwoColumn,
  LazySlicesHyattLeftRightBlockSuite,
  LazySlicesHyattLeftRightCurveBlock,
  LazySlicesHyattLeftRightHalfBlock,
  LazySlicesHyattRainbowBar,
  LazySlicesHyattSpecialOffer,
  LazySlicesHyattSwooshMaskedImagesAndColumns,
  LazySlicesHyattTwentyTwentyTwoAllIncluded,
  LazySlicesHyattTwentyTwentyTwoFracturedGallery,
  LazySlicesHyattTwentyTwentyTwoMagazineOverlay,
  LazySlicesHyattTwentyTwentyTwoNavyBlockOverlay,
  LazySlicesHyattTwentyTwentyTwoSignatureTitle,
  LazySlicesHyattTwentyTwentyTwoSlantedGallery,
  LazySlicesHyattTwentyTwentyTwoSmallImageSmallCopy,
  LazySlicesHyattTwentyTwentyTwoSpecialOffer,
  LazySlicesHyattTwentyTwentyTwoTwoImageDescriptionOverlay,
  LazySlicesHyattSmallPiecesSuiteDetails,
  LazySlicesInfinityLuxuryContactForm,
  LazySlicesInfinityLuxuryInfinityHero,
  LazySlicesInfinityLuxuryInfinityTextSlice,
  LazySlicesInfinityLuxuryMagazineLayout,
  LazySlicesInfinityLuxuryBookingMemberBookingWidget,
  LazySlicesKimptonAllIncluded,
  LazySlicesKimptonBanner,
  LazySlicesKimptonCopyCard,
  LazySlicesKimptonExpandableContentBlock,
  LazySlicesKimptonHero,
  LazySlicesKimptonHorizontalCard,
  LazySlicesKimptonImageCard,
  LazySlicesKimptonIntro,
  LazySlicesKimptonMagazineCard,
  LazySlicesKimptonPromoGrid,
  LazySlicesKimptonScrollingGallery,
  LazySlicesKimptonScrollingGalleryImage,
  LazySlicesKimptonStandardContentBlock,
  LazySlicesKimptonStandardCopy,
  LazySlicesKimptonTwoColumns,
  LazySlicesPanamaJackAlwaysIncluded,
  LazySlicesPlayaCaresFullWidthCountries,
  LazySlicesPlayaCaresFullWidthStats,
  LazySlicesPlayaCollectionAllIncluded,
  LazySlicesPlayaCollectionAwardsSlider,
  LazySlicesPlayaCollectionBlockOverImage,
  LazySlicesPlayaCollectionCallToAction,
  LazySlicesPlayaCollectionCenteredTextParallax,
  LazySlicesPlayaCollectionColorBarTitleParagraph,
  LazySlicesPlayaCollectionContentGrid,
  LazySlicesPlayaCollectionFeatures,
  LazySlicesPlayaCollectionGridItemTitleDescription,
  LazySlicesPlayaCollectionHero,
  LazySlicesPlayaCollectionHotelList,
  LazySlicesPlayaCollectionTestimonial,
  LazySlicesPlayaCollectionThreeImageCards,
  LazySlicesPlayaCollectionTitleDescription,
  LazySlicesPlayaCollectionTwoPaneTextImage,
  LazySlicesPlayaCollectionBookingMemberBookingWidget,
  LazySlicesPlayaresortsCheckInOutTimes,
  LazySlicesPlayaresortsContactRow,
  LazySlicesPlayaresortsImageTitleCopyLink,
  LazySlicesPlayaresortsLeftRightBlockSuite,
  LazySlicesPlayaresortsLeftRightImageWithText,
  LazySlicesPlayaresortsPlayaresortsLeftRightBlockSuite,
  LazySlicesPlayaresortsTitleWysiwyg,
  LazySlicesPlayaresortsVerticalImageCard,
  LazySlicesPlayaresortsWeWantYourInput,
  LazySlicesPlayaresortsWideImageBgSmallWhiteBox,
  LazySlicesPlayaresortsBlogPostsBlogAll,
  LazySlicesPlayaresortsBlogPostsBlogPostDetails,
  LazySlicesPlayaresortsBrandsRowsWithFilters,
  LazySlicesPlayaresortsPressReleasesPressReleasesAll,
  LazySlicesPlayaresortsPressReleasesPressReleasesSingle,
  LazySlicesPlayaresortsPrivacyPolicyTable,
  LazySlicesPlayaresortsPrivacyPolicyTableRow,
  LazySlicesPlayaresortsResortsMeetingFacilitiesExplorer,
  LazySlicesPlayaresortsSpecialOffersPageSpecials,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferBlock,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferCard,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferGrid,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferHorizontal,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferSlider,
  LazySlicesSanctuaryContact,
  LazySlicesSanctuaryFadeColorBlock,
  LazySlicesSanctuaryFeathersDivider,
  LazySlicesSanctuaryFullWidthSpecialOffer,
  LazySlicesSanctuaryGoldPromotion,
  LazySlicesSanctuaryImageBlock,
  LazySlicesSanctuaryIncludedWithStay,
  LazySlicesSanctuaryIntroductionsTitleParagraph,
  LazySlicesSanctuaryLeftRightHalfBlock,
  LazySlicesSanctuaryRoomListing,
  LazySlicesSanctuarySpecialOffer,
  LazySlicesSanctuaryTwoColumnCopyAndImage,
  LazySlicesSanctuaryVideoTakeover,
  LazySlicesSandpiperAboutRoomes,
  LazySlicesSandpiperAccordionCopy,
  LazySlicesSandpiperActivityGrid,
  LazySlicesSandpiperActivityGridCard,
  LazySlicesSandpiperAlertBlock,
  LazySlicesSandpiperBackgroundShape,
  LazySlicesSandpiperColorCardGrid,
  LazySlicesSandpiperColorCardGridCard,
  LazySlicesSandpiperCopyBesideImages,
  LazySlicesSandpiperGeneralTitleAndCopy,
  LazySlicesSandpiperIncludedWithStay,
  LazySlicesSandpiperMap,
  LazySlicesSandpiperRoomType,
  LazySlicesSandpiperRoomTypeFilter,
  LazySlicesSandpiperRoomTypes,
  LazySlicesSandpiperSandpiperHeroImage,
  LazySlicesSandpiperSandpiperSpecialOfferSlider,
  LazySlicesSeadustContentSeadustBlog,
  LazySlicesSeadustContentSeadustBlogDetails,
  LazySlicesSeadustContentSeadustConferenceRoom,
  LazySlicesSeadustContentSeadustConferenceRoomFeature,
  LazySlicesSeadustContentSeadustContactForm,
  LazySlicesSeadustContentSeadustFullWidthGallery,
  LazySlicesSeadustContentSeadustFullWidthGalleryImage,
  LazySlicesSeadustContentSeadustGallery,
  LazySlicesSeadustContentSeadustGalleryList,
  LazySlicesSeadustContentSeadustGalleryListBlock,
  LazySlicesSeadustContentSeadustGenericFullWidthBgWhiteBox,
  LazySlicesSeadustContentSeadustGenericListItem,
  LazySlicesSeadustContentSeadustGenericListItemImage,
  LazySlicesSeadustContentSeadustMap,
  LazySlicesSeadustContentSeadustRestaurantList,
  LazySlicesSeadustContentSeadustRestaurantListBlock,
  LazySlicesSeadustContentSeadustRightParagraphGridImages,
  LazySlicesSeadustContentSeadustRoomDetails,
  LazySlicesSeadustContentSeadustSafeVideoNavigation,
  LazySlicesSeadustContentSeadustSandyThreeColGrid,
  LazySlicesSeadustContentSeadustSandyThreeColGridItem,
  LazySlicesSeadustContentSeadustServicesIconsList,
  LazySlicesSeadustContentSeadustServicesIconsListItem,
  LazySlicesSeadustContentSeadustSitemap,
  LazySlicesSeadustContentSeadustTitleParagraph,
  LazySlicesSeadustContentSeadustWeddingsForm,
  LazySlicesSeadustFormsSeadustConferenceRoomRequestForm,
  LazySlicesSeadustGraphicalLinksSeadustFiftyFiftyImageLinks,
  LazySlicesSeadustGraphicalLinksSeadustFiveImageGridLinks,
  LazySlicesSeadustSlidersFullWidth,
  LazySlicesSeadustSlidersFullWidthSlide,
  LazySlicesSeadustSlidersSeadustBlocksSlide,
  LazySlicesSeadustSlidersSeadustBlocksSlider,
  LazySlicesSeadustSlidersSeadustLeftAlignContentSlide,
  LazySlicesSeadustSlidersSeadustLeftAlignContentWithSlider,
  LazySlicesSeadustSlidersSeadustParalaxBlocksSlide,
  LazySlicesSeadustSlidersSeadustParalaxBlocksSlider,
  LazySlicesSeadustThreeSixtyToursSeadustSpaziousTour,
  LazySlicesWeddingsBgSideContentCallToAction,
  LazySlicesWeddingsCard,
  LazySlicesWeddingsContactDetails,
  LazySlicesWeddingsFullWidthImage,
  LazySlicesWeddingsPackagesSlide,
  LazySlicesWeddingsPackagesSlider,
  LazySlicesWeddingsPatternedGrid,
  LazySlicesWeddingsTitleParagraphDivider,
  LazySlicesWeddingsTwoColumnIntroduction,
  LazySlicesWeddingsVenueSlide,
  LazySlicesWeddingsVenueSlider,
  LazySlicesWyndhamCardOverImages,
  LazySlicesWyndhamDualImageCards,
  LazySlicesWyndhamFullWidthImageCardOnBottom,
  LazySlicesWyndhamHideSignUpToSave,
  LazySlicesWyndhamHideWyndhamInlineBookingWidget,
  LazySlicesWyndhamInlineBookingWidget,
  LazySlicesWyndhamTallSixImageCards,
  LazySlicesWyndhamTallThreeImageCards
     }
      

const resolvedComponent = components[`Lazy${props.slice.component}`]

const { isTopLevelWindow } = useUtils()
const sliceId = computed(() => {
  return `voix-slice-${props.slice.id}`
})

const adminStore = useAdminStore()
const sliceStore = useSliceStore()
const sliceHelper = useSlice()
const sliceConfig = sliceStore.getSlice(props.slice.component)
const pageStore = usePageStore()

const fieldsProxy: Ref<Fields | null> = ref(null)

// Field Data
if (sliceConfig && props.slice.fields)
  fieldsProxy.value = sliceHelper.resolveFields(sliceConfig, props.slice.fields)

// We need to watch the fields for edits so that we can update the
// fieldsProxy value. This is necessary because we need to be able to
// be reactive to changes in the fields.
watch(
  () => props.slice,
  () => {
    if (sliceConfig && props.slice.fields)
      fieldsProxy.value = sliceHelper.resolveFields(sliceConfig, props.slice.fields)
  },
  { deep: true },
)

const { $voix } = useNuxtApp()
const sliceRef = useTemplateRef('sliceRef')
provide('sliceRef', sliceRef)

const fullTailwindConfig = $voix.options.tailwind.config

// Are there styles across any of the breakpoints
const hasStyles = computed(() => {
  const hasStylesObj = {
    paddingTop: false,
    paddingRight: false,
    paddingBottom: false,
    paddingLeft: false,
    marginTop: false,
    marginRight: false,
    marginBottom: false,
    marginLeft: false,
    color: false,
    backgroundColor: false,
  }

  // Loop through all the breakpoints and see if there is a style set
  if (props.slice.properties?.settings) {
    const settings = props.slice.properties.settings
    settings.forEach((breakpoint) => {
      if (breakpoint.padding?.top)
        hasStylesObj.paddingTop = true

      if (breakpoint.padding?.right)
        hasStylesObj.paddingRight = true

      if (breakpoint.padding?.bottom)
        hasStylesObj.paddingBottom = true

      if (breakpoint.padding?.left)
        hasStylesObj.paddingLeft = true

      if (breakpoint.margin?.top)
        hasStylesObj.marginTop = true

      if (breakpoint.margin?.right)
        hasStylesObj.marginRight = true

      if (breakpoint.margin?.bottom)
        hasStylesObj.marginBottom = true

      if (breakpoint.margin?.left)
        hasStylesObj.marginLeft = true

      if (breakpoint.color)
        hasStylesObj.color = true

      if (breakpoint.backgroundColor)
        hasStylesObj.backgroundColor = true
    })
  }

  return hasStylesObj
})

function containsClass(classArray: Array<string>, className: string) {
  const classes = classArray.find((item) => {
    if (typeof item === 'string') {
    // Use startsWith to check if the string starts with the className
      return item.startsWith(className)
    }
    return false
  })
  if (classes) {
    // remove the className from the string
    const descriptorValue = classes.replace(className, '')
    if (descriptorValue) {
      if (!Number.isNaN(Number(descriptorValue))) {
        return fullTailwindConfig.theme.spacing[descriptorValue]
      }
      else {
        // Split the colors by dash and crawl the fullTailwindConfig.theme.colors object
        return descriptorValue.split('-').reduce((acc, key) => {
          if (acc[key]) {
            return acc[key]
          }
          return acc
        }, fullTailwindConfig.theme.colors)
      }
    }
  }
  return null
}

const finalSliceClasses: ComputedRef<Array<string>> = computed(() => {
  const finalClasses = []
  if (hasStyles.value.paddingTop)
    finalClasses.push('voix-slice-padding-top')
  if (hasStyles.value.paddingRight)
    finalClasses.push('voix-slice-padding-right')
  if (hasStyles.value.paddingBottom)
    finalClasses.push('voix-slice-padding-bottom')
  if (hasStyles.value.paddingLeft)
    finalClasses.push('voix-slice-padding-left')
  if (hasStyles.value.marginTop)
    finalClasses.push('voix-slice-margin-top')
  if (hasStyles.value.marginRight)
    finalClasses.push('voix-slice-margin-right')
  if (hasStyles.value.marginBottom)
    finalClasses.push('voix-slice-margin-bottom')
  if (hasStyles.value.marginLeft)
    finalClasses.push('voix-slice-margin-left')
  if (hasStyles.value.color)
    finalClasses.push('voix-slice-color')
  if (hasStyles.value.backgroundColor)
    finalClasses.push('voix-slice-background-color')

  return finalClasses
})

// Slice Styles
const sliceStyles: Ref<{
  [key: string]: string | number
}> = ref({})

function generateSliceStyles() {
  const theSlice = Object.assign({}, props.slice)

  if (!props.slice)
    return true

  let styles: Record<string, string | number> = {}

  if (theSlice.properties && theSlice.properties.settings) {
    const breakpointSettings = findBreakpointSettings(
      theSlice.properties.settings,
    ) as SliceSettingsInterface | null

    if (breakpointSettings) {
      if (breakpointSettings.position)
        styles.position = breakpointSettings.position

      styles = {
        ...styles,
        ...buildFlexStyles(styles, breakpointSettings),
        ...buildGridStyles(styles, breakpointSettings),
        ...buildMaxWidthStyles(styles, breakpointSettings),
        ...buildMarginStyles(styles, breakpointSettings),
        ...buildPaddingStyles(styles, breakpointSettings),
        ...buildColorStyles(styles, breakpointSettings),
      }
    }
  }
  Object.keys(hasStyles.value).forEach((key) => {
    if ((styles[key] === null || styles[key] === undefined) && hasStyles.value[key]) {
      // Ok, there is a style for this property on some breakpoint but not at this breakpoint. Let's apply whatever
      // we can find from the classes on the slice root.
      if (sliceRef.value?.$el) {
        const classList = Array.from(sliceRef.value.$el.classList) as Array<string>

        // calculate if any of the classList Items include part of a string 'p-'
        if (key === 'paddingTop' && containsClass(classList, 'pt-'))
          styles.paddingTop = containsClass(classList, 'pt-')
        else if (key === 'paddingTop' && containsClass(classList, 'py-'))
          styles.paddingTop = containsClass(classList, 'py-')
        else if (key === 'paddingTop' && containsClass(classList, 'p-'))
          styles.paddingTop = containsClass(classList, 'p-')

        if (key === 'paddingRight' && containsClass(classList, 'pr-'))
          styles.paddingRight = containsClass(classList, 'pr-')
        else if (key === 'paddingRight' && containsClass(classList, 'px-'))
          styles.paddingRight = containsClass(classList, 'px-')
        else if (key === 'paddingRight' && containsClass(classList, 'p-'))
          styles.paddingRight = containsClass(classList, 'p-')

        if (key === 'paddingBottom' && containsClass(classList, 'pb-'))
          styles.paddingBottom = containsClass(classList, 'pb-')
        else if (key === 'paddingBottom' && containsClass(classList, 'py-'))
          styles.paddingBottom = containsClass(classList, 'py-')
        else if (key === 'paddingBottom' && containsClass(classList, 'p-'))
          styles.paddingBottom = containsClass(classList, 'p-')

        if (key === 'paddingLeft' && containsClass(classList, 'pl-'))
          styles.paddingLeft = containsClass(classList, 'pl-')
        else if (key === 'paddingLeft' && containsClass(classList, 'px-'))
          styles.paddingLeft = containsClass(classList, 'px-')
        else if (key === 'paddingLeft' && containsClass(classList, 'p-'))
          styles.paddingLeft = containsClass(classList, 'p-')

        if (key === 'marginTop' && containsClass(classList, 'mt-'))
          styles.marginTop = containsClass(classList, 'mt-')
        else if (key === 'marginTop' && containsClass(classList, 'my-'))
          styles.marginTop = containsClass(classList, 'my-')
        else if (key === 'marginTop' && containsClass(classList, 'm-'))
          styles.marginTop = containsClass(classList, 'm-')

        if (key === 'marginRight' && containsClass(classList, 'mr-'))
          styles.marginRight = containsClass(classList, 'mr-')
        else if (key === 'marginRight' && containsClass(classList, 'mx-'))
          styles.marginRight = containsClass(classList, 'mx-')
        else if (key === 'marginRight' && containsClass(classList, 'm-'))
          styles.marginRight = containsClass(classList, 'm-')

        if (key === 'marginBottom' && containsClass(classList, 'mb-'))
          styles.marginBottom = containsClass(classList, 'mb-')
        else if (key === 'marginBottom' && containsClass(classList, 'my-'))
          styles.marginBottom = containsClass(classList, 'my-')
        else if (key === 'marginBottom' && containsClass(classList, 'm-'))
          styles.marginBottom = containsClass(classList, 'm-')

        if (key === 'marginLeft' && containsClass(classList, 'ml-'))
          styles.marginLeft = containsClass(classList, 'ml-')
        else if (key === 'marginLeft' && containsClass(classList, 'mx-'))
          styles.marginLeft = containsClass(classList, 'mx-')
        else if (key === 'marginLeft' && containsClass(classList, 'm-'))
          styles.marginLeft = containsClass(classList, 'm-')

        if (key === 'color' && containsClass(classList, 'text-'))
          styles.color = containsClass(classList, 'text-')

        if (key === 'backgroundColor' && containsClass(classList, 'bg-'))
          styles.backgroundColor = containsClass(classList, 'bg-')
      }
    }
  })

  sliceStyles.value = Object.assign({}, styles)

  return true
}
// Initial generation of styles
generateSliceStyles()
const updateSliceStyles = useDebounceFn(
  generateSliceStyles,
  10,
  { maxWait: 50 },
)

onMounted(() => {
  generateSliceStyles()
})

// Reset styles on window resize
const { width: windowWidth, height: windowHeight } = useWindowSize()
watch([windowWidth, windowHeight], () => {
  updateSliceStyles()
})

const settings = computed(() => {
  if (props.slice.properties && props.slice.properties.settings)
    return props.slice.properties.settings

  return {}
})

watch(settings, () => {
  updateSliceStyles()
}, { deep: true })

function openContextMenu(e: MouseEvent) {
  if (isTopLevelWindow()) {
    e.preventDefault()

    adminStore.setCurrentContextItem(props.slice as ElementInterface, e.clientX, e.clientY)
  }
}

function selectCurrentElement(e: MouseEvent) {
  if (isTopLevelWindow()) {
    e.preventDefault()
    pageStore.setCurrentState('currentlySelectedElements', [props.slice])
  }
}

// Pass this down through PageNavigationSlice to PageNavigationField
provide(
  'slice',
  computed(() => {
    return props.slice
  }),
)

// TODO: Is this necessary? It may cause confusion in devtools
provide('fields', fieldsProxy.value)
provide('mountOpen', true)

const { elementInView } = useElementInView(props.slice, sliceRef)
const { highlightStyles, inStudio, onBounding } = useHighlightElement(props.slice, sliceId, sliceStyles)

const boundingOptions = {
  reset: true,
  windowResize: true,
  windowScroll: true,
  immediate: false,
  updateTiming: 'sync',
}

// This figures out if the slice is the topElement
const topElement = inject('topElement', null)
const isFirstElement = ref(false)
const parentIsFirstElement = inject('parentIsFirstElement', false)

if (topElement?.id === props.slice.id) {
  isFirstElement.value = true
}
provide('parentIsFirstElement', isFirstElement.value || parentIsFirstElement)
</script>

<template>
  <template
    v-if="resolvedComponent"
  >
    <!-- {{ fullTailwindConfig.theme.spacing['24'] }}
      <template v-if="sliceRef">
        {{ sliceRef.$el.classList }}
      </template> -->
    <component
      :is="resolvedComponent"
      :id="slice.properties?.dom_id || sliceId"
      :key="slice.properties?.dom_id || sliceId"
      ref="sliceRef"
      v-element-bounding="[onBounding, boundingOptions]"
      :slice="slice"
      :fields="fieldsProxy"
      :in-view="elementInView"
      v-bind="$attrs"
      :class="finalSliceClasses"
      @click.right="openContextMenu"
      @click.meta.exact="selectCurrentElement"
    >
      <template v-for="{ name } in sliceConfig?.slots" #[name]>
        <slot :name="name" />
      </template>
    </component>

    <div
      v-if="inStudio"
      class="fixed z-[9999] box-border transition-opacity duration-200 pointer-events-none"
      :style="highlightStyles"
    />
  </template>
  <DevOnly v-else>
    The component {{ slice.component }} cannot be found. Are you sure you named it correctly?
  </DevOnly>
</template>

<style scoped>
.voix-slice-margin-top {
  margin-top: v-bind("sliceStyles.marginTop");
}

.voix-slice-margin-right {
  margin-right: v-bind("sliceStyles.marginRight");
}

.voix-slice-margin-bottom {
  margin-bottom: v-bind("sliceStyles.marginBottom");
}

.voix-slice-margin-left {
  margin-left: v-bind("sliceStyles.marginLeft");
}

.voix-slice-padding-top {
  padding-top: v-bind("sliceStyles.paddingTop");
}

.voix-slice-padding-right {
  padding-right: v-bind("sliceStyles.paddingRight");
}

.voix-slice-padding-bottom {
  padding-bottom: v-bind("sliceStyles.paddingBottom");
}

.voix-slice-padding-left {
  padding-left: v-bind("sliceStyles.paddingLeft");
}

.voix-slice-color {
  color: v-bind("sliceStyles.color");
}

.voix-slice-background-color {
  background-color: v-bind("sliceStyles.backgroundColor");
}
</style>
